import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Badge, Card, Row, Col, Spinner, InputGroup, Form } from "react-bootstrap";
import axios from "axios";
import config from '../../Common/Configurations/APIConfig';

const SalonsList = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [salons, setSalons] = useState([]);
    const [filteredSalons, setFilteredSalons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    
    const THEME_COLOR = '#065f46';

    useEffect(() => {
        // Get search term from URL query parameters
        const queryParams = new URLSearchParams(location.search);
        const term = queryParams.get('q') || '';
        setSearchTerm(term);
        
        loadSalons(term);
    }, [location.search]);

    const loadSalons = async (searchTerm) => {
        try {
            setLoading(true);
            // Fetch salons with pagination parameters
            const res = await axios.get(config.APIACTIVEURL + config.GETSALONS, {
                params: { pageSize: 100, pageNumber: 1 }
            });
            
            let allSalons = [];
            if (res.data && res.data.data) {
                allSalons = res.data.data || [];
            }
            
            // Filter only active salons
            const activeSalons = allSalons.filter(salon => salon.status === true);
            setSalons(activeSalons);
            
            // Filter salons based on search term
            if (searchTerm.trim()) {
                const results = activeSalons.filter(salon =>
                    salon.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    salon.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    salon.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    salon.email?.toLowerCase().includes(searchTerm.toLowerCase())
                );
                setFilteredSalons(results);
            } else {
                setFilteredSalons(activeSalons);
            }
        } catch (error) {
            console.error("Error fetching salons", error);
            setSalons([]);
            setFilteredSalons([]);
        } finally {
            setLoading(false);
        }
    };

    const handleBackClick = () => {
        navigate(-1);
    };

    const handleNewSearch = (e) => {
        e.preventDefault();
        const newSearchTerm = e.target.search.value.trim();
        if (newSearchTerm) {
            navigate(`/salons/search?q=${encodeURIComponent(newSearchTerm)}`);
        } else {
            navigate('/salons');
        }
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        navigate('/salons/search');
    };

    const formatTimeZone = (tz) => {
        if (!tz) return '';
        // Convert "Asia/Kolkata" to "Asia (Kolkata)"
        const parts = tz.split('/');
        if (parts.length > 1) {
            return `${parts[0]} (${parts[parts.length - 1].replace('_', ' ')})`;
        }
        return tz;
    };

    const handleSalonClick = (salon) => {
        // You can implement salon details view or booking flow here
        console.log('Selected salon:', salon);
        // For example: navigate to salon details page
        // navigate(`/salon/${salon.salonId}`);
    };

    return (
        <div style={{ backgroundColor: '#fafaf9', minHeight: '100vh', paddingTop: '120px' }}>
            <div className="container py-4">
                {/* Search Bar */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="card border-0 shadow-sm p-3 mb-4" style={{ borderRadius: '1rem' }}>
                            <form onSubmit={handleNewSearch}>
                                <div className="input-group">
                                    <span className="input-group-text bg-transparent border-0">
                                        <i className="ri-search-line fs-5" style={{ color: '#065f46' }}></i>
                                    </span>
                                    <input
                                        type="text"
                                        name="search"
                                        className="form-control border-0 shadow-none fw-medium py-3"
                                        placeholder="Search by salon name..."
                                        defaultValue={searchTerm}
                                        autoComplete="off"
                                    />
                                    <button 
                                        type="submit"
                                        className="btn text-white px-4"
                                        style={{ backgroundColor: THEME_COLOR, borderRadius: '0.75rem' }}
                                    >
                                        Search
                                    </button>
                                    {searchTerm && (
                                        <button 
                                            type="button"
                                            className="btn btn-danger ms-2 px-4"
                                            onClick={handleClearSearch}
                                            style={{ borderRadius: '0.75rem' }}
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

                {/* Results Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h2 className="h3 fw-bold mb-0 border-bottom border-4 pb-2" style={{ borderColor: '#d1fae5' }}>
                            {searchTerm ? `Results for "${searchTerm}"` : 'All Salons'}
                        </h2>
                        <p className="text-muted small mt-2">
                            {searchTerm ? 'Showing salons matching your search' : 'Browse all available beauty salons'}
                        </p>
                    </div>
                    <Badge bg="light" text="dark" className="px-3 py-2">
                        {filteredSalons.length} salon{filteredSalons.length !== 1 ? 's' : ''} found
                    </Badge>
                </div>

                {/* Loading State */}
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="text-muted mt-3">Loading salons...</p>
                    </div>
                ) : (
                    <>
                        {/* No Results State */}
                        {filteredSalons.length === 0 ? (
                            <div className="text-center py-5">
                                <div className="mb-4">
                                    <i className="ri-store-2-line fs-1 text-muted"></i>
                                </div>
                                <h4 className="fw-bold text-dark mb-2">
                                    {searchTerm ? 'No salons found' : 'No salons available'}
                                </h4>
                                <p className="text-muted mb-4">
                                    {searchTerm 
                                        ? `No salons match "${searchTerm}". Try different keywords.`
                                        : 'There are currently no salons available in your area.'}
                                </p>
                                
                            </div>
                        ) : (
                            /* Results Grid */
                            <div className="row g-4">
                                {filteredSalons.map(salon => (
                                    <div className="col-12 col-md-6 col-lg-4" key={salon.salonId}>
                                        <div className="card border-0 shadow-sm h-100 hover-shadow" 
                                             style={{ 
                                                 borderRadius: '1.5rem',
                                                 transition: 'transform 0.2s, box-shadow 0.2s',
                                                 cursor: 'pointer'
                                             }}
                                             onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
                                             onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                                             onClick={() => handleSalonClick(salon)}
                                        >
                                            <div className="card-body p-4">
                                                {/* Salon Header */}
                                                <div className="d-flex align-items-start mb-3">
                                                    <div style={{
                                                        width: '60px',
                                                        height: '60px',
                                                        borderRadius: '12px',
                                                        backgroundColor: '#d1fae5',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flexShrink: 0
                                                    }}>
                                                        <i className="ri-store-2-line fs-4" style={{ color: '#065f46' }}></i>
                                                    </div>
                                                    <div className="ms-3 flex-grow-1">
                                                        <h5 className="fw-bold mb-1 text-dark">{salon.name}</h5>
                                                        {/* <Badge bg="light" text="dark" className="mb-2">
                                                            {salon.slug || 'Premium Salon'}
                                                        </Badge> */}
                                                    </div>
                                                </div>
                                                
                                                {/* Salon Details */}
                                                <div className="mb-4">
                                                    {/* <div className="d-flex align-items-center mb-2">
                                                        <i className="ri-map-pin-line me-2 text-muted"></i>
                                                        <span className="text-dark fw-bold">{salon.city}</span>
                                                    </div> */}
                                                    <div className="d-flex align-items-center mb-2">
                                                        <i className="ri-phone-line me-2 text-muted"></i>
                                                        <span className="text-dark">{salon.phone || 'Contact not available'}</span>
                                                    </div>
                                                    <div className="d-flex align-items-center mb-2">
                                                        <i className="ri-mail-line me-2 text-muted"></i>
                                                        <span className="text-dark small text-truncate">{salon.email}</span>
                                                    </div>
                                                    
                                                </div>
                                                
                                                {/* Address */}
                                                <div className="mt-3 pt-3 border-top">
                                                    <p className="text-muted small mb-2">
                                                        <i className="ri-map-pin-line me-1"></i>
                                                        {salon.address ? salon.address : 'Address not provided'}
                                                    </p>
                                                </div>
                                                
                                                {/* Status and Action Button */}
                                                <div className="mt-4 pt-3 border-top">
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        {/* <span className={salon.status ? 'text-success fw-bold' : 'text-danger fw-bold'}>
                                                            {salon.status ? '✓ Open Now' : '✗ Currently Closed'}
                                                        </span> */}
                                                        {/* <button 
                                                            className="btn btn-sm px-3 py-2 rounded-pill text-white fw-bold"
                                                            style={{ backgroundColor: THEME_COLOR }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigate('/book-appointment', { state: { selectedSalon: salon.salonId } });
                                                            }}
                                                        >
                                                            Book Now
                                                        </button> */}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default SalonsList;